// /inbox/link#token=… — the page the reply email's buttons open. It works without signing in:
// it shows what the link will do and acts only when the button here is pressed, because email
// apps and virus scanners open links by themselves.
import { useEffect, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { Alert, Button, Card, Spinner, buttonClass } from "../components/ui";
import { getItem, peekLink, redeemLink, type InboxItem, type LinkPeek } from "../lib/inbox";
import { useSession } from "../lib/session";
import { ItemEditor } from "./Inbox";

function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-dvh">
      <header className="border-b border-line bg-surface">
        <div className="mx-auto flex h-14 max-w-3xl items-center gap-2 px-4 font-semibold tracking-tight">
          <img src="/icons/icon.svg" alt="" className="h-7 w-7" />
          ClauseKeep
        </div>
      </header>
      <main className="mx-auto max-w-3xl space-y-6 px-4 py-8">{children}</main>
    </div>
  );
}


type Done =
  | { kind: "created"; contractId: string }
  | { kind: "discarded" }
  | { kind: "saved"; contractId: string }
  | { kind: "applied"; contractId: string }
  | { kind: "trusted"; email: string };

export function InboxLink() {
  const { me, loading, today } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [token] = useState(() => new URLSearchParams(location.hash.slice(1)).get("token") ?? "");
  const [peek, setPeek] = useState<LinkPeek | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<Done | null>(null);
  const [editing, setEditing] = useState<{ item: InboxItem; today: string } | null>(null);

  useEffect(() => {
    // Keep the token out of the address bar (and so out of history and screenshots).
    if (location.hash) navigate(location.pathname, { replace: true });
    if (!token) {
      setPeek({ state: "unknown" });
      return;
    }
    peekLink(token)
      .then(setPeek)
      .catch((err) => {
        setPeek({ state: "unknown" });
        setError(err instanceof Error ? err.message : "Couldn't open this link.");
      });
    // Once, for the token the page opened with.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Someone signed in to this workspace edits in the app as usual, even with an old link.
  const signedIn = !loading && !!me;
  useEffect(() => {
    if (signedIn && peek?.itemId && peek.purpose === "edit") navigate(`/inbox/${peek.itemId}`, { replace: true });
  }, [signedIn, peek, navigate]);

  async function act() {
    setBusy(true);
    setError(null);
    try {
      const r = await redeemLink(token);
      if (r.done === "edit") setEditing(await getItem({ kind: "link" }).then((x) => ({ item: x.item, today: x.today ?? today })));
      else if (r.done === "discarded") setDone({ kind: "discarded" });
      else if (r.done === "trusted") setDone({ kind: "trusted", email: r.email ?? peek?.item?.from ?? "They" });
      else setDone({ kind: r.done, contractId: r.contractId! });
    } catch (err) {
      setError(err instanceof Error ? err.message : "That didn't work.");
    } finally {
      setBusy(false);
    }
  }

  if (!peek || loading) {
    return (
      <Shell>
        <Spinner />
      </Shell>
    );
  }

  const signInTo = (path: string) => `/signin?next=${encodeURIComponent(path)}`;
  const openInApp = (path: string, label: string) => (
    <Link to={signedIn ? path : signInTo(path)} className={buttonClass("secondary")}>
      {signedIn ? label : `Sign in to ${label.toLowerCase()}`}
    </Link>
  );

  if (done) {
    return (
      <Shell>
        {done.kind === "discarded" ? (
          <Alert tone="info">Discarded. It won't become a contract. You can restore it from your Inbox for 30 days.</Alert>
        ) : done.kind === "trusted" ? (
          <>
            <Alert tone="good">
              Done ✓ Next time {done.email} sends documents, the email with the buttons goes to them, not you. Anything already waiting stays in your Inbox.
            </Alert>
            {openInApp("/settings#email-in", "Manage trusted senders")}
          </>
        ) : (
          <>
            <Alert tone="good">
              {done.kind === "applied" ? "Contract updated ✓" : done.kind === "saved" ? "Saved as a contract ✓" : "Contract created ✓"} We'll remind you before its deadlines.
            </Alert>
            {openInApp(`/contracts/${done.contractId}`, "Open it")}
          </>
        )}
      </Shell>
    );
  }

  if (editing) {
    return (
      <Shell>
        <h1 className="text-2xl font-semibold tracking-tight">Review and save</h1>
        <ItemEditor
          where={{ kind: "link" }}
          item={editing.item}
          today={editing.today}
          signedIn={false}
          onSaved={(contractId) => setDone({ kind: "saved", contractId })}
          onDiscarded={() => setDone({ kind: "discarded" })}
          onCancel={() => setEditing(null)}
        />
      </Shell>
    );
  }

  if (peek.state !== "ok") {
    const itemPath = peek.itemId ? `/inbox/${peek.itemId}` : "/inbox";
    return (
      <Shell>
        {peek.state === "unknown" ? (
          <Alert tone="bad">{error ?? "This link isn't valid. It may have been copied incompletely."}</Alert>
        ) : (
          <>
            <Alert tone="warn">
              {peek.state === "used" ? "This link was already used." : peek.purpose === "edit" ? "This edit link has expired." : "This link has expired."}{" "}
              {signedIn ? "You can do this in your Inbox." : "Sign in to edit it, or to see it in your Inbox."}
            </Alert>
            {openInApp(itemPath, "Open it in the app")}
          </>
        )}
      </Shell>
    );
  }

  const item = peek.item!;
  if (peek.purpose === "trust") {
    const from = item.from ?? "this sender";
    return (
      <Shell>
        <h1 className="text-2xl font-semibold tracking-tight">Trust {from}?</h1>
        <Card className="space-y-2 text-sm">
          <p>
            {from} isn't in your workspace, so the email about what they sent came to you. If you trust them, the next emails go to them instead, with the buttons to create, edit or
            discard the contract themselves, and they can reply “Yes”.
          </p>
          <p className="text-muted">Their contracts are still saved in your workspace and appear in your Inbox. Only trust an address you know, and remove it any time in Settings.</p>
        </Card>
        {error && <Alert tone="bad">{error}</Alert>}
        <Button disabled={busy} onClick={() => void act()}>
          {busy ? "Working…" : `Trust ${from}`}
        </Button>
      </Shell>
    );
  }
  if (item.status !== "waiting") {
    return (
      <Shell>
        <Alert tone="info">{item.status === "accepted" ? "This was already saved as a contract." : "This was discarded."}</Alert>
        {item.contractId ? openInApp(`/contracts/${item.contractId}`, "Open it") : openInApp("/inbox", "Open your Inbox")}
      </Shell>
    );
  }

  const update = item.kind === "update";
  const changes = item.changes ?? [];
  const heading = {
    create: update ? "Save it as a separate contract?" : "Create this contract?",
    discard: update ? "Ignore this update?" : "Discard this document?",
    edit: "Edit before saving",
    apply: changes.length ? `Apply these changes to ${item.targetTitle ?? "the contract"}?` : `Add this file to ${item.targetTitle ?? "the contract"}?`,
  }[peek.purpose!];
  const action = { create: update ? "Save as a separate contract" : "Create contract", discard: update ? "Ignore" : "Discard", edit: "Edit details", apply: changes.length ? "Apply changes" : "Add the file" }[peek.purpose!];
  const shown = peek.purpose === "apply" ? changes : item.details;
  return (
    <Shell>
      <h1 className="text-2xl font-semibold tracking-tight">{heading}</h1>
      <Card className="space-y-2">
        <div className="font-medium">{item.filename}</div>
        {shown.length ? (
          <ul className="list-disc space-y-1 pl-5 text-sm">
            {shown.map((d) => (
              <li key={d}>{d}</li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted">{peek.purpose === "apply" ? "We didn't find any changes to the details." : "We couldn't find the details yet. Edit it to fill them in."}</p>
        )}
      </Card>
      {peek.purpose === "edit" && <p className="text-sm text-muted">This link works once. After you open it, you have an hour to edit and save, without signing in.</p>}
      {error && <Alert tone="bad">{error}</Alert>}
      <div className="flex flex-wrap gap-2">
        <Button variant={peek.purpose === "discard" ? "danger" : "primary"} disabled={busy} onClick={() => void act()}>
          {busy ? "Working…" : action}
        </Button>
        {peek.purpose === "create" && openInApp(`/inbox/${peek.itemId}`, "Edit in the app first")}
      </div>
    </Shell>
  );
}
